import React from 'react';
import { IconButton } from '@cbrebuild/blocks';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Percent from '../../../nucleus/formats/percent';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import formatAsCurrency from '../../../utils/format-as-currency';
import RequiredLabeledItem from './RequiredLabeledItem';
import { validateAddress } from '../voucher-validation';
import { useRegisterRequiredField } from './RequiredFieldsContext';

const AllocationsReview = () => {
  const {
    outsideBrokers, rebates, sharedFees, taxAllocations, voucher, dealId,
    outside_broker_is_percent, tax_is_percent, fee_sharing_is_percent, rebate_is_percent,
  } = useSelector(state => ({
    outsideBrokers: state.outsideBrokerCommissions,
    rebates: state.rebates.rebates,
    sharedFees: state.sharedFees.sharedFees,
    taxAllocations: state.taxAllocations.taxAllocations,
    voucher: state.voucher.voucher,
    dealId: state.deal?.deal?.id,
    outside_broker_is_percent: !!state.voucher.voucher?.outside_broker_is_percent,
    tax_is_percent: !!state.voucher.voucher?.tax_is_percent,
    fee_sharing_is_percent: !!state.voucher.voucher?.fee_sharing_is_percent,
    rebate_is_percent: !!state.voucher.voucher?.rebate_is_percent,
  }));
  const history = useHistory();
  const url = VOUCHER_ROUTES[2].routes[1].path.replace(':id', voucher.deal);
  const emptyState = '--';
  const requiredText = () => {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
    return <span className="voucher-error-text">Required</span>;
  };
  const taxAmountIsMissing = taxAllocation => (
    !Number(taxAllocation.gross_commission) && !Number(taxAllocation.voucher_gross_commission_percent)
  );
  const getOutsideBrokerAddressForReview = vendor => (
    voucher.is_early_invoice && !validateAddress(vendor) ? null : vendor
  );
  const outsideBrokerIsMissing = commission => !voucher.is_early_invoice && (
    !commission.outside_broker_name || !commission.voucher_broker_rep_role || !commission.gross_commission
    || (commission.paid_by_cbre !== true && commission.paid_by_cbre !== false)
    || !validateAddress(commission.vendor)
  );
  // Matches FeeSharingItem.js's own live validation (`invalidVoucherFromDept`/
  // `invalidVoucherToDept`/`invalidGrossCommission`).
  const feeSharingIsMissing = sharedFee => (
    !sharedFee.voucher_from_dept || !sharedFee.voucher_to_dept || !sharedFee.gross_commission
  );
  const rebateIsMissing = rebate => !rebate.gross_commission;

  // Each of these registers the ENTIRE live Allocations sub-section (its "Was there any
  // X?" header + Yes/No radio toggle + every row + its own "Add X" button) as ONE panel
  // entry, instead of one entry per row -- so the panel is visually and behaviorally
  // identical to the live Financials tab (same radio buttons, same Add button) rather than
  // showing bare row fields with no section context (2026-08-04 feedback).
  useRegisterRequiredField('outside-brokers-section', {
    isReview: true,
    missing: (outsideBrokers.commissions || []).some(outsideBrokerIsMissing),
    buildEntry: () => ({
      section: 'Allocations',
      label: 'Outside Brokers',
      customRowType: 'outsideBrokersSection',
      rowProps: { dealId, voucherId: voucher.id, outside_broker_is_percent },
    }),
  }, [outsideBrokers.commissions, voucher.is_early_invoice, dealId, voucher.id, outside_broker_is_percent]);

  useRegisterRequiredField('tax-allocations-section', {
    isReview: true,
    missing: (taxAllocations || []).some(taxAllocation => !taxAllocation.tax_allocation_name || taxAmountIsMissing(taxAllocation)),
    buildEntry: () => ({
      section: 'Allocations',
      label: 'Tax Allocations',
      customRowType: 'taxAllocationsSection',
      rowProps: { dealId, voucherId: voucher.id, tax_is_percent },
    }),
  }, [taxAllocations, dealId, voucher.id, tax_is_percent]);

  useRegisterRequiredField('fee-sharing-section', {
    isReview: true,
    missing: (sharedFees || []).some(feeSharingIsMissing),
    buildEntry: () => ({
      section: 'Allocations',
      label: 'Fee Sharing',
      customRowType: 'feeSharingSection',
      rowProps: { dealId, voucherId: voucher.id, fee_sharing_is_percent },
    }),
  }, [sharedFees, dealId, voucher.id, fee_sharing_is_percent]);

  useRegisterRequiredField('rebates-section', {
    isReview: true,
    missing: (rebates || []).some(rebateIsMissing),
    buildEntry: () => ({
      section: 'Allocations',
      label: 'Rebates',
      customRowType: 'rebatesSection',
      rowProps: { dealId, voucherId: voucher.id, rebate_is_percent },
    }),
  }, [rebates, dealId, voucher.id, rebate_is_percent]);

  return (
    <div className="voucher-review-group allocations-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>Allocations</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        {outsideBrokers.commissions?.length === 0 && <>There were no outside brokers in this deal.</>}
        {outsideBrokers.commissions?.map((commission, index) => (
          <div className="two-cols" key={commission.id}>
            <div className="col-left">
              <h3>Outside Broker #{index + 1}</h3>
              <RequiredLabeledItem label="Full Name" item={commission.outside_broker_name} notRequired={voucher.is_early_invoice} isReview />
              <RequiredLabeledItem label="Rep Role" item={commission.voucher_broker_rep_role || null} notRequired={voucher.is_early_invoice} isReview />
              <RequiredLabeledItem label="License Expiration Date" item={commission.license_expdt} notRequired isReview />
              <RequiredLabeledItem label="License #" item={commission.license_code} notRequired isReview />
              <RequiredLabeledItem
                label="Amount"
                item={commission.gross_commission
                  ? `${formatAsCurrency(commission.gross_commission)} (${commission.voucher_gross_commission_percent || 0}%)`
                  : null}
                notRequired={voucher.is_early_invoice}
                isReview
              />
            </div>
            <div className="col-right">
              <h3>Details</h3>
              <RequiredLabeledItem label="Company" item={commission.vendor?.name} notRequired isReview />
              <RequiredLabeledItem label="Pre-Agreement ID" item={commission.vendor?.pre_agreement_id} notRequired isReview />
              <RequiredLabeledItem label="Address" item={getOutsideBrokerAddressForReview(commission.vendor)} format="address" notRequired={voucher.is_early_invoice} isReview />
              <RequiredLabeledItem label="Is this outside broker a CBRE affiliate?" item={commission.partner ? 'Yes' : 'No'} notRequired isReview />
              <RequiredLabeledItem
                label="How is this outside broker being paid?"
                item={(commission.paid_by_cbre && 'Paid by CBRE') || (commission.paid_by_cbre === false && 'Paid directly') || null}
                notRequired={voucher.is_early_invoice}
                isReview
              />
            </div>
          </div>
        ))}
        {taxAllocations?.length > 0 &&
          <>
            <h3>Taxes</h3>
            <table>
              <colgroup>
                <col name="tax" />
                <col name="amount" />
              </colgroup>
              <tbody>
                <tr className="borderless-row">
                  <th>Tax</th>
                  <th>Amount</th>
                </tr>
                {taxAllocations.map(taxAllocation => (
                  <tr key={taxAllocation.id}>
                    <td>{taxAllocation.tax_allocation_name || requiredText()}</td>
                    <td>{taxAmountIsMissing(taxAllocation) ? requiredText() : formatAsCurrency(taxAllocation.gross_commission || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }
        {sharedFees?.length > 0 &&
          <>
            <h3>Fee Sharing</h3>
            <table>
              <colgroup>
                <col name="from" />
                <col name="to lob" />
                <col />
                <col name="allocation" />
                <col name="amount" />
                <col name="rem emp id" />
              </colgroup>
              <tbody>
                <tr className="borderless-row">
                  <th>From</th>
                  <th>To LOB</th>
                  <th />
                  <th>Allocation</th>
                  <th>Amount</th>
                  <th>REM EMP ID</th>
                </tr>
                {sharedFees.map(sharedFee => (
                  <tr key={sharedFee.id}>
                    <td>{sharedFee.voucher_from_dept || requiredText()}</td>
                    <td>{sharedFee.voucher_to_dept || requiredText()}</td>
                    <td>&nbsp;</td>
                    <td><Percent value={Number(sharedFee.voucher_gross_commission_percent)} emptyState="" /></td>
                    <td>{formatAsCurrency(sharedFee.gross_commission || 0)}</td>
                    <td>{sharedFee.voucher_rem_employee_id || emptyState}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }
        {rebates?.length > 0 &&
          <>
            <h3>Rebates</h3>
            <table>
              <colgroup>
                <col name="prv - client name" />
                <col />
                <col name="allocation" />
                <col name="amount" />
              </colgroup>
              <tbody>
                <tr className="borderless-row">
                  <th>RV - Client Name</th>
                  <th />
                  <th>Allocation</th>
                  <th>Amount</th>
                </tr>
                {rebates.map(rebate => (
                  <tr key={rebate.id}>
                    <td>{rebate.rebate_client_name}</td>
                    <td>&nbsp;</td>
                    <td><Percent value={Number(rebate.voucher_gross_commission_percent)} emptyState="" /></td>
                    <td>{formatAsCurrency(rebate.gross_commission || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }
      </div>
    </div>
  );
};

export default AllocationsReview;
